import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { applyTheme, fetchTheme, QUANTA_DEFAULTS } from "@/lib/theme";

const ThemeContext = createContext({
  theme: QUANTA_DEFAULTS,
  loading: true,
  refresh: async () => {},
});

/**
 * Wraps the app so that every route sees the resolved tenant theme.
 * Applies CSS vars + <title> + favicon on mount and on refresh.
 *
 * If `tenantSlug` is not passed as a prop, we look for `?tenant_slug=`
 * in the current URL as a preview fallback (Phase 11c §7). Custom-domain
 * resolution happens server-side via the Host header — no client hint
 * is needed for that path.
 */
export function ThemeProvider({ tenantSlug, children }) {
  const [theme, setTheme] = useState(QUANTA_DEFAULTS);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    let slug = tenantSlug;
    if (!slug && typeof window !== "undefined") {
      const q = new URLSearchParams(window.location.search);
      slug = q.get("tenant_slug") || undefined;
    }
    const data = await fetchTheme(slug);
    const resolved = data?.resolved || QUANTA_DEFAULTS;
    setTheme(resolved);
    applyTheme(resolved);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSlug]);

  const value = useMemo(
    () => ({ theme, loading, refresh: load }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, loading],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
